import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormContextData } from './types';
import { StepMachine, useStepMachine } from './step-machine';
import {
  clearLocalStorage,
  useLocalStorage,
  writeToLocalStorage,
} from './local-storage';
import { getStepConfiguration } from './step-configuration';

export type FormContext = {
  hasInitialized: boolean;
  data: FormContextData;
  setData: React.Dispatch<React.SetStateAction<FormContextData>>;
} & StepMachine;

const FormContext = createContext<FormContext>(undefined!);

export function FormContextProvider({ children }: PropsWithChildren) {
  const localStorageData = useLocalStorage();
  const [data, setData] = useState<FormContextData>(localStorageData.data);
  const [hasInitialized, setHasInitialized] = useState(false);

  const { step, state, loading, onNext, onPrevious, registerValidator } =
    useStepMachine(localStorageData.step, data);
  const { isFinal } = getStepConfiguration(step);

  useEffect(() => {
    setData(localStorageData.data);
    setHasInitialized(true);
  }, [localStorageData]);

  useEffect(() => {
    if (isFinal) {
      clearLocalStorage();
      return;
    }
    writeToLocalStorage({ data, step });
  }, [data, step, isFinal]);

  const setDataWrapper = useCallback(
    (newData: React.SetStateAction<FormContextData>) => {
      const searchParams = new URLSearchParams(window.location.search);
      const opportunityId = searchParams.get('opportunityId');
      const leadId = searchParams.get('leadId');

      if (typeof newData === 'function') {
        setData(prevData => {
          const updatedData = newData(prevData);
          if (opportunityId) {
            updatedData.opportunityId = opportunityId;
          }
          if (leadId) {
            updatedData.leadId = leadId;
          }
          return updatedData;
        });
      } else {
        if (opportunityId) {
          newData.opportunityId = opportunityId;
        }
        if (leadId) {
          newData.leadId = leadId;
        }
        setData(newData);
      }
    },
    [setData],
  );

  const value = useMemo(() => {
    return {
      data,
      setData: setDataWrapper,
      step,
      state,
      loading,
      onNext,
      onPrevious,
      registerValidator,
      hasInitialized,
    } satisfies FormContext;
  }, [
    data,
    hasInitialized,
    setDataWrapper,
    step,
    state,
    loading,
    onNext,
    onPrevious,
    registerValidator,
  ]);
  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
}

export function useFormContext() {
  return useContext(FormContext);
}
